import { useState } from 'react';
import { updateSubscriptionCache } from 'src/apollo/cache/update';
import { usePermissionsContext } from 'src/components/permissions/PermissionsContext';
import YesNoDialog from 'src/components/yes-no-dialog/YesNoDialog';
import { useUpgradeFromTrialMutation } from 'src/generated/graphql';
import useApolloErrorHandler from 'src/hooks/useApolloErrorHandler';

export default function UpgradeSubscriptionDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { permissions } = usePermissionsContext();

  const pluralizedEpisode = permissions?.episodeLimit === 1 ? 'episode' : 'episodes';

  const initialDialogText =
    (permissions?.episodeLimit ?? 0 > 1)
      ? `You can create ${permissions?.episodeLimit} ${pluralizedEpisode} during your trial. Upgrade to a paid plan, and you can create as many episodes as you'd like.`
      : `Your first episode is on us. To create unlimited episodes, you'll need to upgrade to a paid plan.`;

  const [{ noBtnText, yesBtnText, dialogText, dialogType, dialogTitle }, setDialogContent] =
    useState({
      dialogTitle: 'Upgrade',
      dialogType: 'trial-warning',
      noBtnText: 'No, thanks',
      yesBtnText: 'Upgrade now!',
      dialogText: initialDialogText,
    });

  const [upgradeFromTrial, { loading, client, error }] = useUpgradeFromTrialMutation({
    onCompleted: res => {
      updateSubscriptionCache({ subscription: res.upgradeFromTrial, client });
      onClose();
    },
  });
  useApolloErrorHandler(error);

  async function onUpgrade() {
    if (dialogType === 'trial-warning') {
      setDialogContent({
        dialogTitle: 'Are you sure?',
        dialogType: 'ensure-upgrade',
        noBtnText: 'No',
        yesBtnText: 'Yes, I am sure',
        dialogText:
          'Are you sure you want to upgrade your subscription? You will be charged immediately.',
      });
      return;
    }

    upgradeFromTrial();
  }

  return (
    <YesNoDialog
      dialogTitle={dialogTitle}
      open={open}
      noBtnText={noBtnText}
      yesBtnText={yesBtnText}
      dialogText={dialogText}
      noHandler={onClose}
      yesHandler={onUpgrade}
      disabled={loading}
    />
  );
}
